import { FirehoseClient, PutRecordCommand, PutRecordCommandInput } from '@aws-sdk/client-firehose';
import {
  fromCognitoIdentityPool,
  fromCognitoIdentity,
  CognitoIdentityCredentials,
} from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromBase64 } from '@aws-sdk/util-base64-browser';
import { ENV } from '@user-portal/helpers';

import Utils from '../helpers/Utils';
import AbstractSystem from './AbstractSystem';
import { Config } from '../Interfaces/Config';
import {
  ITrackNewAnalytics,
  IPurchaseNew,
  IPrepareTrackParams,
  EventDataTrack,
  PurchaseData,
} from '../Interfaces/Events';
import { SYSTEMS_NAMES } from '../enums';
import DataProvider from '../DataProvider';

import { EventActionType } from '../types';

const REGION = 'us-east-1';

export default class AwsAnalytic extends AbstractSystem {
  private streamName: string;
  private envMode: string;
  private firehose: FirehoseClient | undefined;
  private releaseDate: string;

  constructor() {
    super(SYSTEMS_NAMES.amazon, false);

    this.streamName = 'aws_stream_name';
    this.envMode = ENV.develop;
    this.releaseDate = 'default_date';
    this.firehose = undefined;
  }

  public init({ id, config }: { id: string; config?: Config }) {
    try {
      if (!this.getIsInit() && config?.streamName) {
        Utils.setReleaseDate(config.releaseDate);

        this.streamName = config.streamName;
        this.envMode = config.envMode || ENV.develop;
        this.releaseDate = config.releaseDate;

        const client = new CognitoIdentityClient({ region: REGION });

        const identityId = config.awsId as string;

        const credentials = identityId
          ? fromCognitoIdentity({
              identityId,
              client,
            })
          : fromCognitoIdentityPool({
              identityPoolId: id,
              client,
            });

        this.firehose = new FirehoseClient({
          region: REGION,
          credentials,
        });

        this.isInit = true;
      }
    } catch (e) {
      DataProvider.handleError(e);
    }
  }

  public async track(eventName: string, eventData: EventDataTrack | PurchaseData = {}): Promise<void> {
    if (this.firehose !== undefined) {
      const command = new PutRecordCommand(await this.prepareTrackParams({ eventName, eventData }));
      try {
        await this.firehose.send(command);

        console.log(`Event ${eventName} successfully sent.`);
      } catch (err) {
        DataProvider.handleError(err);

        console.error(`Error while sending ${eventName}`);

        // @ts-ignore
        console.error(err, err.stack);
      }
    }
  }

  public async trackNew({ eventName, eventAction, eventData = {} }: ITrackNewAnalytics): Promise<void> {
    if (this.firehose !== undefined) {
      const command = new PutRecordCommand(await this.prepareTrackParams({ eventName, eventAction, eventData }));

      try {
        await this.firehose.send(command);

        console.log(`Event ${eventName} successfully sent.`);
      } catch (err) {
        DataProvider.handleError(err);

        console.error(`Error while sending ${eventName}`);
        // @ts-ignore
        console.error(err, err.stack);
      }
    }
  }

  public async purchase(event_name: string, data: PurchaseData) {
    await this.track(event_name || 'purchase', data);
  }

  public async purchaseNew({ eventName, data }: IPurchaseNew) {
    const eventNamePurchase = eventName || 'purchase';
    await this.trackNew({ eventName: eventNamePurchase, eventData: data });
  }

  public async getAwsId(): Promise<string> {
    const credentials = (await this.firehose?.config.credentials()) as CognitoIdentityCredentials;

    return credentials.identityId;
  }

  public async getDefaultRecordData(eventName = 'purchase', eventAction?: EventActionType) {
    const awsData = await Utils.getAnalyticData(eventName, eventAction);
    const aws_id = await this.getAwsId();

    const OnetrustActiveGroups = Utils.formatOptanonGroups(window?.OptanonActiveGroups);

    return {
      ...awsData,
      aws_id,
      OnetrustActiveGroups,
    };
  }

  private async prepareTrackParams({
    eventName,
    eventAction,
    eventData,
  }: IPrepareTrackParams): Promise<PutRecordCommandInput> {
    const recordData = await this.getDefaultRecordData(eventName, eventAction);

    const finalRecordData = Object.assign({}, recordData, eventData, {
      mode: this.envMode,
      release_date: this.releaseDate,
    });

    const stringifyRecordData = JSON.stringify(finalRecordData);
    const convertData = btoa(unescape(encodeURIComponent(stringifyRecordData)));

    console.log('BEFORE SEND TO AMAZON DATA', finalRecordData);

    return {
      DeliveryStreamName: this.streamName,
      Record: { Data: fromBase64(convertData) },
    };
  }
}
